import { FilterFilled } from "@ant-design/icons";
import { Button, Popover, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import classNames from "classnames";
import { companiesColumnsData, sortDirections } from "shared/constants";
import { CompanyItem } from "shared/api";
import {
  MultipleOptionsTableFilter,
  TableButton,
  TableImage,
  TableRadioGroup,
  TableSearchFilter,
} from "shared/ui";
import { CategoriesSuppliedByCompany } from "pages/Companies/ui/CategoriesSuppliedByCompany";
import { ChildCompanies } from "pages/Companies";
import { CheckboxValueType } from "antd/es/checkbox/Group";

interface GetCompaniesColumnsProps {
  t: Function;
  changeSort: Function;
  editCompany: Function;
  renderCompanyRole: Function;
  showCompanyDeleteModal: Function;
  updateCompany: Function;
  handleNameValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCompanyWithChildrenModal: (record: CompanyItem) => void;
  nameValue: string;
  clearNameValue: () => void;
  clearPaymentTermsValue: () => void;
  handlePaymentTermsValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  paymentTermsValue: string;
  evaluationAsSupplierGte: string;
  evaluationAsSupplierLte: string;
  handleEvaluationAsSupplierGte: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleEvaluationAsSupplierLte: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearEvaluationAsSupplierGte: () => void;
  clearEvaluationAsSupplierLte: () => void;
  handleIncoTermsValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  incoTermsValue: string;
  clearIncoTermsValue: () => void;
  handleCustomerSupplierChange: (selectedValues: CheckboxValueType[]) => void;
  selectedCustomerSupplier: string[];
  categoriesSuppliedData: { label: string; value: number }[];
  handleCategoriesSupplierChange: (value: string | number) => void;
  selectCategoriesSupplier: string | number;
  handleSearchOrdersClick: (companyId: number) => void;
  hasDeletePermission: boolean | undefined;
  contactNameValue: string;
  handleContactNameValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearContactNameValue: () => void;
  childCompanyValue: string;
  handleChildCompanyValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearChildCompanyValue: () => void;
}

const {
  name,
  address,
  incoTerms,
  actions,
  customerOrSupplier,
  image,
  evaluationAsSupplier,
  paymentTerms,
  childCompanies,
  categoriesSupplied,
  contactName,
  information,
} = companiesColumnsData;

export const useGetCompaniesColumns = ({
  t,
  changeSort,
  editCompany,
  renderCompanyRole,
  showCompanyDeleteModal,
  handleCompanyWithChildrenModal,
  nameValue,
  handleNameValue,
  clearNameValue,
  clearPaymentTermsValue,
  handlePaymentTermsValue,
  paymentTermsValue,
  evaluationAsSupplierGte,
  evaluationAsSupplierLte,
  handleEvaluationAsSupplierGte,
  handleEvaluationAsSupplierLte,
  clearEvaluationAsSupplierGte,
  clearEvaluationAsSupplierLte,
  incoTermsValue,
  handleIncoTermsValue,
  clearIncoTermsValue,
  handleCustomerSupplierChange,
  selectedCustomerSupplier,
  categoriesSuppliedData,
  handleCategoriesSupplierChange,
  selectCategoriesSupplier,
  handleSearchOrdersClick,
  hasDeletePermission,
  childCompanyValue,
  clearChildCompanyValue,
  clearContactNameValue,
  contactNameValue,
  handleChildCompanyValue,
  handleContactNameValue,
  updateCompany,
}: GetCompaniesColumnsProps): ColumnsType<CompanyItem> => {
  const handleSwitchChange = async (record: CompanyItem, checked: boolean) => {
    try {
      await updateCompany({
        id: record.id,
        name: record.name,
        address: record.address,
        inco_terms: record.inco_terms,
        is_buyer: checked,
        is_supplier: checked,
        payment_term_net_days: record.payment_term_net_days,
        config_dynamic_order_fields: record?.config_dynamic_order_fields ?? [],
        product_cards: record.product_cards ?? [],
        evaluation_as_supplier: record?.evaluation_as_supplier,
        categories_supplied: record?.categories_supplied?.map((item) => ({
          material_category:
            (item?.material_category as any)?.id || item?.material_category,
        })),
        parent_company: record?.parent_company,
        child_companies: record?.child_companies?.map((company: any) =>
          typeof company === "number" ? company : company.id
        ),
        vat_number: record?.vat_number?.length ? record.vat_number : null,
        email: record?.email?.length ? record.email : null,
        phone: record?.phone?.length ? record.phone : null,
        contact_name: record?.contact_name?.length ? record.contact_name : null,
        information: record?.information?.length ? record.information : null,
        report_packaging: record?.report_packaging,
        report_material_item: record?.report_material_item,
        invoice_mapping_name: record?.invoice_mapping_name,
      }).unwrap();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to update company status:", error);
    }
  };

  return [
    {
      title: t(image.title),
      render: (company: CompanyItem) => (
        <TableImage image={company?.image as string} />
      ),
    },
    {
      title: t(name.title),
      render: (company: CompanyItem) => (
        <Button
          className="border-none hover:border-none bg-transparent shadow-none"
          onClick={() => editCompany(company)}
        >
          <span className="underline">{company?.name}</span>
        </Button>
      ),
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("name", "-name");
        },
      }),
      sorter: true,
      sortDirections,
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600  scale-125": nameValue.length,
          })}
        />
      ),
    },
    {
      title: t(name.title),
      render: (company: CompanyItem) => (
        <Button
          className="border-none hover:border-none bg-transparent shadow-none"
          onClick={() => editCompany(company)}
        >
          <span className="underline">{company?.name}</span>
        </Button>
      ),
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("name", "-name");
        },
      }),
      sorter: true,
      sortDirections,
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600  scale-125": nameValue.length,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearNameValue}
          confirm={confirm}
          value={nameValue!}
          handleFilterChange={handleNameValue!}
          visible={visible}
          placeholder={t("companies.filter-placeholder")!}
          title={t("companies.filter-title")}
        />
      ),
    },
    {
      title: t(address.title),
      dataIndex: address.dataIndex,
      key: address.key,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("address", "-address");
        },
      }),
      sorter: true,
      sortDirections,
    },
    {
      title: t(evaluationAsSupplier.title),
      dataIndex: evaluationAsSupplier.dataIndex,
      key: evaluationAsSupplier.key,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("evaluation_as_supplier", "-evaluation_as_supplier");
        },
      }),
      sorter: true,
      sortDirections,
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <div>
          <TableSearchFilter
            setSelectedKeys={(selectedKeys: React.Key[]) => {
              setSelectedKeys([selectedKeys[0], evaluationAsSupplierLte]);
            }}
            clearFilters={() => {
              clearEvaluationAsSupplierGte();
              clearEvaluationAsSupplierLte();
            }}
            confirm={confirm}
            value={evaluationAsSupplierGte}
            handleFilterChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleEvaluationAsSupplierGte(e);
            }}
            visible={visible}
            placeholder={t("Greater or Equal")}
            title={t("Filter by Evaluation")}
          />
          <TableSearchFilter
            setSelectedKeys={(selectedKeys: React.Key[]) => {
              setSelectedKeys([evaluationAsSupplierGte, selectedKeys[0]]);
            }}
            clearFilters={() => {
              clearEvaluationAsSupplierGte();
              clearEvaluationAsSupplierLte();
            }}
            confirm={confirm}
            value={evaluationAsSupplierLte}
            handleFilterChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleEvaluationAsSupplierLte(e);
            }}
            visible={visible}
            placeholder={t("Lesser or Equal")}
            title={t("Filter by Evaluation")}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125":
              evaluationAsSupplierGte.length || evaluationAsSupplierLte.length,
          })}
        />
      ),
    },
    {
      title: t(paymentTerms.title),
      dataIndex: paymentTerms.dataIndex,
      key: paymentTerms.key,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("payment_term_net_days", "-payment_term_net_days");
        },
      }),
      sorter: true,
      sortDirections,
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": paymentTermsValue.length,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearPaymentTermsValue}
          confirm={confirm}
          value={paymentTermsValue}
          handleFilterChange={handlePaymentTermsValue}
          visible={visible}
          placeholder={t("Enter Payment Terms")}
          title={t("Filter by Payment Terms")}
        />
      ),
    },
    {
      title: t(incoTerms.title),
      dataIndex: incoTerms.dataIndex,
      key: incoTerms.key,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("inco_terms", "-inco_terms");
        },
      }),
      sorter: true,
      sortDirections,
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearIncoTermsValue}
          confirm={confirm}
          value={incoTermsValue}
          handleFilterChange={handleIncoTermsValue}
          visible={visible}
          placeholder={t("Search INCO Terms")}
          title={t("Filter by INCO Terms")}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": paymentTermsValue.length,
          })}
        />
      ),
    },
    {
      title: t(customerOrSupplier.title),
      render: (item: CompanyItem) => renderCompanyRole(t, item),
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("is_supplier", "-is_supplier");
          changeSort("is_buyer", "-is_buyer");
        },
      }),
      sorter: true,
      sortDirections,
      filterDropdown: ({ clearFilters }) => (
        <MultipleOptionsTableFilter
          options={[
            { label: t("companiesColumns.isSupplier"), value: "is_supplier" },
            { label: t("companiesColumns.isBuyer"), value: "is_buyer" },
          ]}
          selectedValues={selectedCustomerSupplier}
          handleChange={handleCustomerSupplierChange}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": selectedCustomerSupplier.length > 0,
          })}
        />
      ),
      filteredValue: selectedCustomerSupplier,
    },
    {
      title: t(categoriesSupplied.title),
      render: (item: CompanyItem) => (
        <CategoriesSuppliedByCompany item={item} />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": !!selectCategoriesSupplier,
          })}
        />
      ),
      filterDropdown: () => (
        <TableRadioGroup
          options={categoriesSuppliedData}
          selectedValue={selectCategoriesSupplier}
          onChange={handleCategoriesSupplierChange}
        />
      ),
    },
    {
      title: t(childCompanies.title),
      render: (item: CompanyItem) => (
        <ChildCompanies
          company={item}
          handleCompanyWithChildrenModal={handleCompanyWithChildrenModal}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": childCompanyValue.length > 0,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearChildCompanyValue}
          confirm={confirm}
          value={childCompanyValue}
          handleFilterChange={handleChildCompanyValue}
          visible={visible}
          placeholder={t("Enter child company name")}
          title={t("Filter by Child Company")}
        />
      ),
    },

    {
      title: t(contactName.title),
      render: (item: CompanyItem) => item?.contact_name,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("contact_name", "-contact_name");
        },
      }),
      sorter: true,
      sortDirections,
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": contactNameValue.length > 0,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearContactNameValue}
          confirm={confirm}
          value={contactNameValue}
          handleFilterChange={handleContactNameValue}
          visible={visible}
          placeholder={t("Enter contact name")}
          title={t("Filter by Contact Name")}
        />
      ),
    },

    {
      title: t(information.title),
      onHeaderCell: () => ({
        onClick: () => {
          changeSort("information", "-information");
        },
      }),
      sorter: true,
      sortDirections,
      render: (item: CompanyItem) => (
        <div
          style={{
            maxWidth: 400,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {item?.information}
        </div>
      ),
    },
    {
      title: t(actions.title),
      key: actions.key,

      render: (record: CompanyItem) => {
        // Switch is enabled if company is either buyer or supplier
        const isCompanyActive = record.is_buyer || record.is_supplier;

        return (
          <div className="w-full gap-1 flex items-center justify-around">
            <TableButton
              type="search"
              onClick={() => handleSearchOrdersClick(record.id)}
            />
            <Popover
              content={
                isCompanyActive ? "Company is Active" : "Company is Passive"
              }
              trigger="hover"
            >
              <Switch
                style={{
                  backgroundColor: isCompanyActive ? "limegreen" : "tomato",
                }}
                checked={isCompanyActive}
                onChange={(checked) => handleSwitchChange(record, checked)}
              />
            </Popover>
            {hasDeletePermission && (
              <TableButton
                type="delete"
                onClick={() => showCompanyDeleteModal(record)}
              />
            )}
          </div>
        );
      },
    },
  ];
};
